/**
 * MCP client connections — Settings → Integrations
 * Owner + Finance only; Admin cannot connect.
 */

import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../../../context/AuthContext'
import { Button } from '../../../components/ui/button'
import {
  loadMcpConnections,
  loadMcpClients,
  loadMcpSetup,
  loadMcpActivity,
  revokeMcpConnection,
  loadMcpPersonalAccessTokens,
  createMcpPersonalAccessToken,
  revokeMcpPersonalAccessToken,
  type McpConnection,
  type McpPersonalAccessToken,
} from '../../../api/mcp'

export default function McpIntegrationsPanel() {
  const { user } = useAuth()
  const qc = useQueryClient()
  const [busy, setBusy] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [setupClient, setSetupClient] = useState<string | null>(null)
  const [setupJson, setSetupJson] = useState<string | null>(null)
  const [setupInstructions, setSetupInstructions] = useState<string | null>(null)
  const [tab, setTab] = useState<'clients' | 'pat' | 'activity'>('clients')
  const [issuedToken, setIssuedToken] = useState<string | null>(null)

  useEffect(() => {
    if (tab !== 'pat') setIssuedToken(null)
  }, [tab])

  const canConnect = user?.company_role === 'owner' || user?.company_role === 'finance'

  const { data: connections = [], refetch } = useQuery({
    queryKey: ['mcp-connections'],
    queryFn: loadMcpConnections,
    enabled: canConnect,
    staleTime: 30_000,
    retry: 1,
  })

  const { data: clients = [] } = useQuery({
    queryKey: ['mcp-clients'],
    queryFn: loadMcpClients,
    enabled: canConnect,
    staleTime: 60_000,
  })

  const { data: patRows = [], refetch: refetchPat } = useQuery({
    queryKey: ['mcp-pat'],
    queryFn: loadMcpPersonalAccessTokens,
    enabled: canConnect && tab === 'pat',
    staleTime: 15_000,
  })

  const { data: activity = [] } = useQuery({
    queryKey: ['mcp-activity'],
    queryFn: () => loadMcpActivity(),
    enabled: canConnect && tab === 'activity',
    staleTime: 15_000,
  })

  async function handleRevoke(conn: McpConnection) {
    if (!window.confirm(`Revoke ${conn.client_name}?`)) return
    setBusy(`revoke-${conn.id}`)
    setErr(null)
    try {
      await revokeMcpConnection(conn.id)
      await refetch()
      qc.invalidateQueries({ queryKey: ['mcp-activity'] })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function handleCreatePat() {
    if (!window.confirm('Create a new personal access token? Any existing token stays valid until revoked.')) return
    setBusy('pat-create')
    setErr(null)
    setIssuedToken(null)
    try {
      const data = await createMcpPersonalAccessToken()
      setIssuedToken(data.token)
      await refetchPat()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function handleRevokePat(row: McpPersonalAccessToken) {
    if (!window.confirm('Revoke this personal access token?')) return
    setBusy(`pat-revoke-${row.id}`)
    setErr(null)
    try {
      await revokeMcpPersonalAccessToken(row.id)
      await refetchPat()
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  async function handleSetup(clientId: string) {
    setBusy(`setup-${clientId}`)
    setErr(null)
    try {
      const data = await loadMcpSetup(clientId)
      setSetupClient(clientId)
      setSetupJson(data.config_json)
      setSetupInstructions(data.instructions)
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setBusy(null)
    }
  }

  if (!canConnect) {
    return (
      <div className="panel" data-testid="panel-mcp-disabled" style={{ marginTop: 14 }}>
        <div className="panel-h">
          <div className="panel-tt">AI clients (MCP)</div>
          <span className="panel-st">Claude Desktop, ChatGPT, and Cursor</span>
        </div>
        <div style={{ padding: '14px 16px', fontSize: 13, color: 'var(--ink3)' }}>
          MCP connections require an owner or finance login. Admin accounts cannot connect external clients.
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="panel" data-testid="panel-mcp" style={{ marginTop: 14 }}>
        <div className="flex gap-2 px-4 pt-3">
          <Button type="button" variant={tab === 'clients' ? 'navy' : 'outline'} data-testid="mcp-tab-clients" onClick={() => setTab('clients')}>
            Clients
          </Button>
          <Button type="button" variant={tab === 'pat' ? 'navy' : 'outline'} data-testid="mcp-tab-pat" onClick={() => setTab('pat')}>
            Access token
          </Button>
          <Button type="button" variant={tab === 'activity' ? 'navy' : 'outline'} data-testid="mcp-tab-activity" onClick={() => setTab('activity')}>
            Activity
          </Button>
        </div>
        <div className="panel-h">
          <div className="panel-tt">AI clients (MCP)</div>
          <span className="panel-st">
            Connect Claude Desktop, ChatGPT, or Cursor — or any app that registers via OAuth. Connected apps appear below by name.
          </span>
        </div>
        {tab === 'clients' && (<>

        {connections.length === 0 ? (
          <div className="int-row">
            <div className="int-ic" style={{ background: '#1e3a5f', color: '#93c5fd' }}>AI</div>
            <div>
              <div className="int-tt">No clients connected</div>
              <div className="int-st">Read-only access — projects, pay apps, AR, and weekly cash</div>
            </div>
          </div>
        ) : (
          connections.map((c) => (
            <div className="int-row" key={c.id} data-testid={`mcp-connection-${c.id}`}>
              <div className="int-ic" style={{ background: '#1e3a5f', color: '#93c5fd' }}>AI</div>
              <div>
                <div className="int-tt">{c.client_name}</div>
                <div className="int-st">
                  {c.scope_labels.join(' · ')}
                  {c.last_used_at ? ` · last used ${new Date(c.last_used_at).toLocaleString()}` : ''}
                </div>
              </div>
              <button
                type="button"
                className="btn b-red btn-sm"
                data-testid={`mcp-revoke-${c.id}`}
                disabled={!!busy}
                onClick={() => handleRevoke(c)}
              >
                Revoke
              </button>
            </div>
          ))
        )}

        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line)' }}>
          <div className="set-row-tt" style={{ marginBottom: 8 }}>Connect a client</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {clients.map((c) => (
              <button
                key={c.client_id}
                type="button"
                className="btn b-ol btn-sm"
                data-testid={`mcp-setup-${c.client_id}`}
                disabled={!!busy}
                onClick={() => handleSetup(c.client_id)}
              >
                {c.name}
              </button>
            ))}
          </div>
          {setupJson && setupClient && (
            <div style={{ marginTop: 12 }} data-testid="mcp-setup-config">
              <div className="set-row-st" style={{ marginBottom: 6 }} data-testid="mcp-setup-instructions">
                {setupInstructions}
              </div>
              <div className="set-row-st" style={{ marginBottom: 6 }}>
                Copy this config for {setupClient}:
              </div>
              <pre style={{
                background: 'var(--bg2)', padding: 12, borderRadius: 8, fontSize: 11,
                overflow: 'auto', maxHeight: 240,
              }}>
                {setupJson}
              </pre>
              <button
                type="button"
                className="btn b-dk btn-sm"
                style={{ marginTop: 8 }}
                onClick={() => navigator.clipboard.writeText(setupJson)}
              >
                Copy config
              </button>
            </div>
          )}
        </div>
        </>)}

        {tab === 'pat' && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid var(--line)' }} data-testid="mcp-pat-panel">
          <div className="set-row-st" style={{ marginBottom: 10 }}>
            Personal access tokens connect Muse and other custom MCP clients with <strong>read</strong> and <strong>draft</strong> scopes only. Act tools (submit, send, confirm) require OAuth.
          </div>
          {patRows.map((row) => (
            <div className="int-row" key={row.id} data-testid={`mcp-pat-${row.id}`}>
              <div className="int-ic" style={{ background: '#3d4450', color: '#e8e6e3' }}>PAT</div>
              <div>
                <div className="int-tt">Token #{row.id}</div>
                <div className="int-st">
                  {row.scope_labels?.join(' · ') || row.scopes.join(' · ')}
                  {row.last_used_at ? ` · last used ${new Date(row.last_used_at).toLocaleString()}` : ' · never used'}
                </div>
              </div>
              <button
                type="button"
                className="btn b-red btn-sm"
                disabled={!!busy}
                onClick={() => handleRevokePat(row)}
              >
                Revoke
              </button>
            </div>
          ))}
          <button type="button" className="btn b-dk btn-sm" data-testid="mcp-pat-create" disabled={!!busy} onClick={handleCreatePat}>
            Create personal access token
          </button>
          {issuedToken && (
            <div style={{ marginTop: 12 }} data-testid="mcp-pat-shown-once">
              <div className="set-row-st" style={{ marginBottom: 6, color: 'var(--red)' }}>Copy now — this token is shown once.</div>
              <pre style={{ background: 'var(--bg2)', padding: 12, borderRadius: 8, fontSize: 11, overflow: 'auto' }}>{issuedToken}</pre>
              <button type="button" className="btn b-ol btn-sm" style={{ marginTop: 8 }} onClick={() => navigator.clipboard.writeText(issuedToken)}>
                Copy token
              </button>
            </div>
          )}
        </div>
        )}

        {tab === 'activity' && (
        <div style={{ padding: '10px 16px 14px', borderTop: '1px solid var(--line)' }}>
            <div data-testid="mcp-activity-list" style={{ marginTop: 10, fontSize: 11 }}>
              {activity.length === 0 ? (
                <div className="set-row-st">No MCP tool calls yet.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--ink3)' }}>
                      <th>When</th><th>Client</th><th>Tool</th><th>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activity.slice(0, 50).map((r) => (
                      <tr key={r.id}>
                        <td>{new Date(r.created_at).toLocaleString()}</td>
                        <td>{r.client_name}</td>
                        <td>{r.tool}</td>
                        <td>{r.result}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              <a href="/api/mcp/activity/export" className="btn b-ol btn-sm" style={{ marginTop: 8, display: 'inline-block' }}>
                Export CSV
              </a>
            </div>
        </div>
        )}

        {err && (
          <div style={{ padding: '8px 16px 12px', fontSize: 11, color: 'var(--red)' }}>{err}</div>
        )}
      </div>
    </>
  )
}
